import { InteractiveTutorial, InteractiveTutorialProps } from "@/features/interactive_tutorial";
import { useGetOrgQuery } from "@/store/services/supabase";
import { BellOutlined, MoreOutlined } from "@ant-design/icons";
import { Image, Space, TourStepProps } from "antd";
export const PortfolioExportTutorial = (props: InteractiveTutorialProps<Record<any, any>>) => {
  const {
    data: org
  } = useGetOrgQuery();
  const assetLevelSteps: TourStepProps[] = org?.is_large_bank ? [{
    title: "Asset-Level Results",
    description: <Space direction="vertical">
              <p>
                Click <b>Asset Level</b> to download a .csv file of metrics for each asset within the portfolio.
              </p>
              <Image alt="Indication to click the 'Asset Level' button on a complete portfolio." src="/img/tours/portfolio-insights/step_10.jpg" />
            </Space>
  }] : [{
    title: "Asset-Level Results",
    description: <Space direction="vertical">
              <p>
                Click <b>Asset Level</b> to view options for downloading each asset within the portfolio.
              </p>
              <Image alt="Indication to click the 'Asset Level' button on a complete portfolio." src="/img/tours/portfolio-insights/step_10.jpg" />
              <p>Select the output and file type desired.</p>
              <Image wrapperClassName="mx-auto block max-w-[32rem]" alt="List of options which can be selected." src="/img/tours/portfolio-insights/step_11.jpg" />
            </Space>
  }];
  const steps: TourStepProps[] = [{
    title: "Click 'Analyse New Portfolio'.",
    description: <Space direction="vertical">
          <p>
            Under <b>Comprehensive Analysis Tools</b>, click on <b>Portfolio Insights</b>, and then click{" "}
            <b>New Portfolio</b>, or download previous Portfolio Insights files from the library.
          </p>
          <Image alt="Indication to click the button titled 'New Portfolio'." src="/img/tours/portfolio-insights/step_1.jpg" />
        </Space>
  }, {
    title: "Click 'Download Template .csv'.",
    description: <Space direction="vertical">
          <p>
            Click <b>Download Template .csv</b> and enter your portfolio data into the template provided, being careful
            to follow input specifications.
          </p>
          <div className="flex w-full justify-center">
            <Image className="max-w-[24rem]" alt="Indication to click the 'Download Template .csv' button to obtain a demonstration file." src="/img/tours/portfolio-insights/step_2.jpg" />
          </div>
        </Space>
  }, {
    title: "Upload",
    description: <Space direction="vertical">
          <p>
            Once finished, save the template file to your desktop, then click <b>Select .csv file</b> and select your
            newly created portfolio file.
          </p>
          <div className="flex w-full justify-center">
            <Image className="max-w-[24rem]" alt="Indication to upload file using 'Select .csv file' box." src="/img/tours/portfolio-insights/step_3.jpg" />
          </div>
        </Space>
  }, {
    title: "Review",
    description: <Space direction="vertical">
          <p>
            A modal will appear confirming the details of your upload. Here you can name the analysis, select sharing
            permissions and see a total asset count for your uploaded portfolio file. If you select <b>Private</b> your
            portfolio will be only visible to you, while shared portfolios will be visible to other users in the
            selected group.
          </p>
          <p>You can also check your current usage against the asset allocation available to your organisation.</p>
          <div className="flex w-full justify-center">
            <Image className="max-w-[24rem]" alt="Confirmation step before continuing with upload." src="/img/tours/portfolio-insights/step_5.jpg" />
          </div>
          <p>
            Click <b>Run Analysis</b> to run the Portfolio, or <b>Back</b> to exit.
          </p>
        </Space>
  }, {
    title: "Processing",
    description: <Space direction="vertical">
          <p>
            The Portfolio will begin to run, with its status changed to blue and <b>Processing</b>.
          </p>
          <p>
            To enable a notification in your browser once the analysis is completed, click on the Alarm Bell{" "}
            <BellOutlined /> icon and follow the security prompts on your operating system to enable notifications.
          </p>
          <div className="flex w-full justify-center">
            <Image alt="Display of portfolio in list in processing step." src="/img/tours/portfolio-insights/step_7.jpg" />
          </div>
        </Space>
  }, {
    title: "Completion",
    description: <Space direction="vertical">
          <p>Once processing is complete, the status will change to a green tick.</p>
          <Image alt="Indication of tick to identify complete portfolio." src="/img/tours/portfolio-insights/step_8.jpg" />
        </Space>
  }, {
    title: "Summary Results",
    description: <Space direction="vertical">
          <p>
            Click <b>Portfolio Summary</b> to download a .xlsx summary file that contains a whole-of-portfolio overview.
          </p>
          <Image alt="Indication to click the 'Portfolio Summary' button on a complete portfolio." src="/img/tours/portfolio-insights/step_9.jpg" />
        </Space>
  }, ...assetLevelSteps, {
    title: "Editing & Deleting Files",
    description: <Space direction="vertical">
          <p>
            Click the vertical three-dot menu <MoreOutlined /> to edit the name or sharing permissions of your
            portfolio.
          </p>
          <div className="flex w-full justify-center">
            <Space align="center" direction="vertical">
              <Image alt="Display of portfolio editing modal." src="/img/tours/portfolio-insights/step_12a.jpg" />
            </Space>
          </div>
          <p>
            You can also choose to delete any portfolio visible to you or your group. Deleting a portfolio will
            permanently remove it for all members of your organisational group.
          </p>
          <div className="flex w-full justify-center">
            <Space align="center" direction="vertical">
              <Image alt="Display of portfolio deletion modal." src="/img/tours/portfolio-insights/step_12b.jpg" />
            </Space>
          </div>
        </Space>
  }];
  return <InteractiveTutorial<Record<any, any>> isWide={true} steps={steps} {...props} data-sentry-element="InteractiveTutorial" data-sentry-component="PortfolioExportTutorial" data-sentry-source-file="PortfolioExportTutorial.tsx" />;
};